<div cdk-overlay-origin #trigger class="select-container" [class.if-text]="size() === 'text'">
  @if (type() === 'custom-dropdown') {
    <div
      class="custom-dropdown-trigger"
      (click)="toggleDropdown()"
      [attr.aria-expanded]="isOpen"
      aria-haspopup="listbox">
      <ng-content></ng-content>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    </div>
  } @else {
    <button
      type="button"
      class="select-trigger"
      (click)="toggleDropdown()"
      [attr.aria-expanded]="isOpen"
      aria-haspopup="listbox"
      [disabled]="disabled"
      [class.placeholder-visible]="placeholderVisible">
      <span class="select-trigger-label">{{ selectedLabel }}</span>
      @if (selectedBadge) {
        <span class="select-trigger-badge">{{ selectedBadge }}</span>
      }
    </button>
  }
  @if ((multi() && selected && selected.length > 0 && !disabled) || (clearable() && selected && !multi() && !disabled)) {
    <button type="button" class="select-clear" (click)="clearSelection($event)" aria-label="Clear selection">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="size-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
      </svg>
    </button>
  }
  <ng-template
    cdk-connected-overlay
    cdkConnectedOverlayLockPosition
    cdkConnectedOverlayHasBackdrop
    cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayDisableClose]="true"
    [cdkConnectedOverlayOrigin]="trigger"
    [cdkConnectedOverlayOpen]="isOpen"
    [cdkConnectedOverlayPositions]="overlayPositions"
    [cdkConnectedOverlayMinWidth]="originWidth"
    (detach)="isOpen = false"
    (backdropClick)="isOpen = false"
    (overlayOutsideClick)="isOpen = false">
    <div class="select-dropdown" role="listbox" @fadeInOnEnter @slideInUpOnEnter>
      <!-- Search input for typeahead functionality -->
      @if (searchable()) {
        <div class="select-search">
          <app-input
            #searchInput
            [placeholder]="searchPlaceholder()"
            [(ngModel)]="searchValue"
            (ngModelChange)="onSearchValueChange()"
            class="select-search-input">
          </app-input>
          @if (searchTerm.length > 0) {
            <app-button variant="ghost" (click)="clearSearch()" class="select-search-clear" aria-label="Clear search">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </app-button>
          }
        </div>
      }

      <!-- Loading state -->
      @if (loading()) {
        <div class="select-loading">Loading...</div>
      }

      <!-- No results message -->
      @if (!loading() && searchable() && isSearching && currentOptions.length === 0) {
        <div class="select-no-results">No results found</div>
      }

      <!-- Options list -->
      @if (!loading() && currentOptions.length > 0) {
        <div class="select-options" #optionsContainer>
          <!-- Available options -->
          @for (option of availableOptions; track $index) {
            <div
              class="select-option"
              [class.multi]="multi()"
              [class.disabled]="isOptionDisabled(option)"
              role="option"
              [class.highlighted]="isHighlighted(option)"
              [attr.aria-selected]="isSelected(option)"
              [attr.aria-disabled]="isOptionDisabled(option)"
              (click)="selectOption(option)"
              (mouseenter)="highlightedIndex = $index"
              #optionItem>
              @if (multi()) {
                <input type="checkbox" class="select-checkbox" [checked]="isSelected(option)" [disabled]="isOptionDisabled(option)" />
              }
              <span class="select-option-label">{{ bindLabel() ? option[bindLabel()!] : option }}</span>
              @if (bindBadge() && option[bindBadge()!]) {
                <span class="select-option-badge">{{ option[bindBadge()!] }}</span>
              }
            </div>
          }

          <!-- Locked options group -->
          @if (hasLockedOptions) {
            <div class="select-group-header">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lock-icon">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
              <span>Not available in your subscription</span>
            </div>
            @for (option of lockedOptions; track $index) {
              <div
                class="select-option locked"
                [class.multi]="multi()"
                role="option"
                [attr.aria-selected]="isSelected(option)"
                [attr.aria-disabled]="true"
                (click)="selectOption(option)"
                #optionItem>
                @if (multi()) {
                  <input type="checkbox" class="select-checkbox" [checked]="isSelected(option)" [disabled]="true" />
                }
                <span class="select-option-label">{{ bindLabel() ? option[bindLabel()!] : option }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="lock-icon">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
              </div>
            }
          }
        </div>
      }
    </div>
  </ng-template>
</div>
